/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { LicenseKey } from 'app/common/constants/license-key';
import { ProductConfig, SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';
import * as moment from 'moment';

import { type GroupTypeE } from '../global-search-str.service';

export const typeMap = {
    LUNS: 'LUN',
    LUNGROUPS: 'LUN_GROUP',
    NamespaceS: 'NAMESPACE',
    NamespaceGROUPS: 'NAMESPACE_GROUP',
    HOSTS: 'HOST_TYPE',
    HOSTGROUPS: 'HOST_GROUP_TYPE',
    PORTGROUPS: 'PORT_GROUP_TYPE',
    LOGICALPORTS: 'LIF_PORT_TYPE',
    LUNSNAPSHOTS: 'SNAPSHOT',
    FILESYSTEMS: 'FILE_SYSTEM',
};

export const DEFAULT_FILTERS = {
    LUN: [{ field: 'SUBTYPE', matchMode: '::', value: '0_6' }, { field: 'USAGETYPE', value: '5_6_7', matchMode: '!!' }],
    LUNS: [{ field: 'SUBTYPE', matchMode: '::', value: '0_6' }, { field: 'USAGETYPE', value: '5_6_7', matchMode: '!!' }],
    LUNGROUPS: [],
    Namespace: [{ field: 'SUBTYPE', value: '0', matchMode: '::' }, { field: 'USAGETYPE', value: '5_6_7', matchMode: '!!' }],
    NamespaceS: [{ field: 'SUBTYPE', value: '0', matchMode: '::' }, { field: 'USAGETYPE', value: '5_6_7', matchMode: '!!' }],
    NamespaceGROUPS: [],
    HOSTS: [],
    HOSTGROUPS: [],
    PORTGROUPS: [],
    LOGICALPORTS: [], // vstoreId: -1,timeConversion: 0
    VSTORES: [], // vstoreId: -1
    LUNSNAPSHOTS: [{ field: 'SUBTYPE', matchMode: '::', value: '0_6' }], // timeConversion: 0
    FILESYSTEMS: [], // vstoreId: -1
};
export const TIME_SEGMENTS = {
    five_minutes: 300,
    half_hour: 1800,
    one_day: 86400,
    one_week: 604800,
    one_month: 2592000,
    one_year: 31536000,
};

export const LUNMAPUSAGETYPE = {
    0: 'lun',
    1: 'lun',
    3: 'peLun',
    10: 'channelLun',
};

export const LUNMAPSUBTYPE = {
    0: (FeatureConfig.IsReplaceStrLunWithNamespace ? 'Namespace' : 'lun'),
    2: 'peLun',
    7: 'channelLun',
};

/**
 * 数据 type Id
 */
export enum DataTypeId {
    lun = 'LUN',
    lunGroup = 'LUN_GROUP',
    Namespace = 'LUN',
    NamespaceGroup = 'LUN_GROUP',
    host = 'HOST_TYPE',
    hostGroup = 'HOST_GROUP_TYPE',
    clone = 'LUN',
    peLun = 'LUN',
    vvolLun = 'LUN',
    channelLun = 'LUN'
}

// 文件系统/Dtree 共享类型
export enum ShareTypeE {
    Homedir = '1',
    Normal = '0',
    All = '2'
}

const lun_zh = FeatureConfig.IsReplaceStrLunWithNamespace ? 'Namespace' : 'LUN';
const lun_group_zh = FeatureConfig.IsReplaceStrLunWithNamespace ? 'Namespace组' : 'LUN组';
const lun_en = FeatureConfig.IsReplaceStrLunWithNamespace ? 'Namespaces' : 'LUNs';
const lun_group_en = FeatureConfig.IsReplaceStrLunWithNamespace ? 'NamespaceGroups' : 'LUNGroups';
const lun_ja = FeatureConfig.IsReplaceStrLunWithNamespace ? 'Namespaces' : 'LUN';
const lun_group_ja = FeatureConfig.IsReplaceStrLunWithNamespace ? 'NamespaceGroups' : 'LUNグループ';

export const zh_SourceType = [lun_zh, lun_group_zh, '主机', '主机组', '端口组', '逻辑端口', '多租户', 'LUN快照', '文件系统'];
export const en_SourceType = [lun_en, lun_group_en, 'Hosts', 'HostGroups',
    'PortGroups', 'LogicalPorts', 'vStores', 'LUNSnapshots', 'FileSystems'];

export const zh_SourceTypeWithLicense = [
    { name: lun_zh, license: FeatureConfig.IsReplaceStrLunWithNamespace ? [] : [LicenseKey.SmartThin] },
    { name: lun_group_zh, license: FeatureConfig.IsReplaceStrLunWithNamespace ? [] : [LicenseKey.SmartThin] },
    '主机',
    '主机组',
    '端口组',
    '逻辑端口',
    { name: '多租户', isNASFeature: true, license: [LicenseKey.SmartMultiTenant] },
    {
        name: 'LUN快照',
        license: [LicenseKey.SmartThin, [LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP]],
    },
    { name: '文件系统', isNASFeature: true },
];
export const en_SourceTypeWithLicense = [
    { name: lun_en, license: FeatureConfig.IsReplaceStrLunWithNamespace ? [] : [LicenseKey.SmartThin] },
    { name: lun_group_en, license: FeatureConfig.IsReplaceStrLunWithNamespace ? [] : [LicenseKey.SmartThin] },
    'Hosts', 'HostGroups', 'PortGroups', 'LogicalPorts',
    { name: 'vStores', isNASFeature: true, license: [LicenseKey.SmartMultiTenant] },
    {
        name: 'LUNSnapshots',
        license: [LicenseKey.SmartThin, [LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP]],
    },
    { name: 'FileSystems', isNASFeature: true },
];
export const ja_SourceTypeWithLicense = [
    { name: lun_ja, license: FeatureConfig.IsReplaceStrLunWithNamespace ? [] : [LicenseKey.SmartThin] },
    { name: lun_group_ja, license: FeatureConfig.IsReplaceStrLunWithNamespace ? [] : [LicenseKey.SmartThin] },
    'ホスト', 'HostGroups', 'PortGroups', 'LogicalPorts',
    { name: 'vStores', isNASFeature: true, license: [LicenseKey.SmartMultiTenant] },
    {
        name: 'LUNスナップショット',
        license: [LicenseKey.SmartThin, [LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP]],
    },
    { name: 'ファイルシステム', isNASFeature: true },
];

export const SourceTypeWithLicense = {
    zh: zh_SourceTypeWithLicense,
    en: en_SourceTypeWithLicense,
    ja: ja_SourceTypeWithLicense,
};

export const DEFAULT_TIP_ZH = [
    {
        normal: FeatureConfig.IsReplaceStrLunWithNamespace ? 'Namespace ' : 'LUN ',
        license: FeatureConfig.IsReplaceStrLunWithNamespace ? [] : [LicenseKey.SmartThin],
    },
    { normal: '文件系统 ', isNASFeature: true },
    {
        normal: FeatureConfig.IsReplaceStrLunWithNamespace ? 'Namespace组 ' : 'LUN组 ',
        license: FeatureConfig.IsReplaceStrLunWithNamespace ? [] : [LicenseKey.SmartThin],
    },
    {
        normal: '端口组 ',
        license: [LicenseKey.SmartThin],
    },
    {
        normal: '主机 ',
        license: [LicenseKey.SmartThin],
    },
    {
        normal: '主机组 ',
        license: [LicenseKey.SmartThin],
    },
    { normal: '多租户 ', isNASFeature: true, license: [LicenseKey.SmartMultiTenant] },
    { normal: '逻辑端口 ' },
    {
        normal: 'LUN快照 ',
        license: [LicenseKey.SmartThin, [LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP]],
    },
];
export const DEFAULT_TIP_EN = [
    {
        normal: FeatureConfig.IsReplaceStrLunWithNamespace ? 'Namespaces ' : 'LUNs ',
        license: FeatureConfig.IsReplaceStrLunWithNamespace ? [] : [LicenseKey.SmartThin],
    },
    { normal: 'FileSystems ', isNASFeature: true },
    {
        normal: FeatureConfig.IsReplaceStrLunWithNamespace ? 'NamespaceGroups ' : 'LUNGroups ',
        license: FeatureConfig.IsReplaceStrLunWithNamespace ? [] : [LicenseKey.SmartThin],
    },
    {
        normal: 'PortGroups ',
        license: [LicenseKey.SmartThin],
    },
    {
        normal: 'Hosts ',
        license: [LicenseKey.SmartThin],
    },
    {
        normal: 'HostGroups ',
        license: [LicenseKey.SmartThin],
    },
    { normal: 'vStores ', isNASFeature: true, license: [LicenseKey.SmartMultiTenant] },
    { normal: 'LogicalPorts ' },
    {
        normal: 'LUNSnapshots ',
        license: [LicenseKey.SmartThin, [LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP]],
    },
];
export const DEFAULT_TIP_JA = [
    {
        normal: FeatureConfig.IsReplaceStrLunWithNamespace ? 'Namespaces' : 'LUN',
        license: FeatureConfig.IsReplaceStrLunWithNamespace ? [] : [LicenseKey.SmartThin],
    },
    { normal: 'ファイルシステム', isNASFeature: true },
    {
        normal: FeatureConfig.IsReplaceStrLunWithNamespace ? 'NamespaceGroups' : 'LUNグループ',
        license: FeatureConfig.IsReplaceStrLunWithNamespace ? [] : [LicenseKey.SmartThin],
    },
    {
        normal: 'PortGroups',
        license: [LicenseKey.SmartThin],
    },
    {
        normal: 'ホスト',
        license: [LicenseKey.SmartThin],
    },
    {
        normal: 'HostGroups',
        license: [LicenseKey.SmartThin],
    },
    { normal: 'vStores', isNASFeature: true, license: [LicenseKey.SmartMultiTenant] },
    { normal: 'LogicalPorts' },
    {
        normal: 'LUNスナップショット',
        license: [LicenseKey.SmartThin, [LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP]],
    },
];

export const DEFAULT_TIP_MAP = {
    zh: DEFAULT_TIP_ZH,
    en: DEFAULT_TIP_EN,
    ja: DEFAULT_TIP_JA,
};

export const NEWFEATURE = {
    en: [
        {
            index: 0,
            type: 'newfeature',
            src: '/assets/images/global-search/banner_smart.png',
            title: 'Container Settings',
            info: 'Enables simple and efficient visualized management of containerized applications.',
            routeType: 'Container',
            active: true,
            permission: ['container_service_R'],
            license: [
                [
                    LicenseKey.SmartContainerLicense,
                    LicenseKey.SmartContainerLicenseStandard,
                    LicenseKey.SmartContainerLicensePremium,
                    LicenseKey.CloudBackup,
                    LicenseKey.AntiRansomware,
                    LicenseKey.ANTIRANSOMWARE_FOR_SAN,
                    LicenseKey.S3,
                ],
            ],
        },
        {
            index: 1,
            type: 'newfeature',
            src: '/assets/images/global-search/banner_ota.png',
            title: 'OTA Upgrade',
            info: 'The new version based on DME IQ is automatically pushed and upgraded with one click.',
            routeType: 'upgrade',
            active: false,
            permission: ['support_major_W'],
            noOem: true,
            noProdRelease: true,
            hide: !FeatureConfig.IsSupportOTAUpgrade || ProductConfig.isHideUpgrade,
        },
        (!FeatureConfig.IsShowIntelligentAlarmHandling || !SWITCH.PRODUCT_SUPPORT_AI) ? null : {
            index: 2,
            type: 'newfeature',
            src: '/assets/images/global-search/banner_alarm_fix.png',
            title: 'Intelligent Alarm Handling',
            info: 'Intelligently detects alarms and locates root causes.',
            routeType: 'alarm',
            active: false,
            isNotSupportFeatureInOceanMicro: SWITCH.IS_DIRECT_MODE,
        },
    ],
    zh: [
        {
            index: 0,
            type: 'newfeature',
            src: '/assets/images/global-search/banner_smart.png',
            title: '容器设置',
            info: '简单、高效的容器应用可视化管理。',
            routeType: 'Container',
            active: true,
            permission: ['container_service_R'],
            license: [
                [
                    LicenseKey.SmartContainerLicense,
                    LicenseKey.SmartContainerLicenseStandard,
                    LicenseKey.SmartContainerLicensePremium,
                    LicenseKey.CloudBackup,
                    LicenseKey.AntiRansomware,
                    LicenseKey.ANTIRANSOMWARE_FOR_SAN,
                    LicenseKey.S3,
                ],
            ],
        },
        {
            index: 1,
            type: 'newfeature',
            src: '/assets/images/global-search/banner_ota.png',
            title: 'OTA升级',
            info: '基于DME IQ的新版本自动推送，一键升级。',
            routeType: 'upgrade',
            active: false,
            permission: ['support_major_W'],
            noOem: true,
            noProdRelease: true,
            hide: !FeatureConfig.IsSupportOTAUpgrade || ProductConfig.isHideUpgrade,
        },
        (!FeatureConfig.IsShowIntelligentAlarmHandling || !SWITCH.PRODUCT_SUPPORT_AI) ? null : {
            index: 2,
            type: 'newfeature',
            src: '/assets/images/global-search/banner_alarm_fix.png',
            title: '智能告警修复',
            info: '智能检测告警和定位根因。',
            routeType: 'alarm',
            active: false,
            isNotSupportFeatureInOceanMicro: SWITCH.IS_DIRECT_MODE,
        },
    ],
    ja: [
        {
            index: 0,
            type: 'newfeature',
            src: '/assets/images/global-search/banner_smart.png',
            title: 'コンテナ設定',
            info: 'コンテナ化されたアプリケーションのシンプルで効率的な視覚化された管理を可能にします。',
            routeType: 'Container',
            active: true,
            permission: ['container_service_R'],
            license: [
                [
                    LicenseKey.SmartContainerLicense,
                    LicenseKey.SmartContainerLicenseStandard,
                    LicenseKey.SmartContainerLicensePremium,
                    LicenseKey.CloudBackup,
                    LicenseKey.AntiRansomware,
                    LicenseKey.ANTIRANSOMWARE_FOR_SAN,
                    LicenseKey.S3,
                ],
            ],
        },
        {
            index: 1,
            type: 'newfeature',
            src: '/assets/images/global-search/banner_ota.png',
            title: 'OTAアップグレード',
            info: 'DME IQに基づく新しいバージョンは、ワンクリックで自動的にプッシュされ、アップグレードされます。',
            routeType: 'upgrade',
            active: false,
            permission: ['support_major_W'],
            noOem: true,
            noProdRelease: true,
            hide: !FeatureConfig.IsSupportOTAUpgrade || ProductConfig.isHideUpgrade,
        },
        (!FeatureConfig.IsShowIntelligentAlarmHandling || !SWITCH.PRODUCT_SUPPORT_AI) ? null : {
            index: 2,
            type: 'newfeature',
            src: '/assets/images/global-search/banner_alarm_fix.png',
            title: 'インテリジェントアラーム処理',
            info: 'アラームをインテリジェントに検出し、根本原因を特定します。',
            routeType: 'alarm',
            active: false,
            isNotSupportFeatureInOceanMicro: SWITCH.IS_DIRECT_MODE,
        },
    ],
};
const quickStartLanguageMap = {
    en: {
        blockService: 'Block Service',
        blockServiceInfo: 'Provisions resources by LUN.',
        fileService: 'File Service',
        fileServiceInfo: 'Provisions resources by file system.',
        dataProtection: 'Data Protection',
        dataProtectionInfo: 'Provides basic and value-added data protection features.',
        managemantAndMonitoring: 'Management and Monitoring',
        managemantAndMonitoringInfo: 'Manages and monitors storage system performance in real time.',
    },
    zh: {
        blockService: '块服务',
        blockServiceInfo: '通过LUN的形式，将存储资源进行分配。',
        fileService: '文件服务',
        fileServiceInfo: '通过文件系统的形式，将存储资源进行分配。',
        dataProtection: '数据保护',
        dataProtectionInfo: '提供了数据保护基础和增值特性相关功能。',
        managemantAndMonitoring: '管理和监控',
        managemantAndMonitoringInfo: '对存储系统性能进行实时的管理和监控。',
    },
    ja: {
        blockService: 'ブロックサービス',
        blockServiceInfo: 'LUNごとにリソースをプロビジョニングします。',
        fileService: 'ファイルサービス',
        fileServiceInfo: 'ファイルシステムごとにリソースをプロビジョニングします。',
        dataProtection: 'データ保護',
        dataProtectionInfo: '基本および付加価値の高いデータ保護機能を提供します。',
        managemantAndMonitoring: '管理とモニタリング',
        managemantAndMonitoringInfo: 'ストレージシステムのパフォーマンスをリアルタイムで管理および監視します。',
    },
};

export const QUICKSTARTJSON = (language: string, isHigh): any => {
    const languageTemp = language === 'zh' ? 'zh_cn' : 'en_us';
    const urlSuffix = {
        blockService: {
            is2910: 'cbaea9ce_779b_8f5c_b675_f9a62ee9c5f2',
            isHigh: '5637a3af_5f1f_eed5_54f8_af5e023bec6f',
            noHigh: 'e8cae21b_d3f8_f169_3e72_457183c6aa22',
        },
        fileService: {
            is2910: '1b0f2721_9628_dcf8_7560_57233ea67381',
            isHigh: '42b0d707_2844_f6b6_f3a9_a4a6d837ca8f',
            noHigh: 'c290ac0e_49b8_44bc_d246_4266034eb974',
        },
        dataProtection: {
            is2910: 'c97c41b5_0085_037a_235c_a14ec51842e5',
            isHigh: '0b42b2ba_1b2b_9ac6_4073_c0bfc2135035',
            noHigh: '4000c72c_96d0_2ac3_3383_78bd3e103051',
        },
        managementAndMonitoring: {
            is2910: '489f6052_3306_e106_6c40_fc85e229a644',
            isHigh: '918c7cd3_fc77_4f2b_76f2_63005d6c44c8',
            noHigh: 'ae21a69f_fd90_2238_ebfa_d9dede7de8a6',
        },
    };
    const highType = isHigh ? 'isHigh' : 'noHigh';
    const type = FeatureConfig.Is2910GlobalSearchQuickStart ? 'is2910' : highType;
    return [
        {
            index: 0,
            type: 'quickstart',
            src: '/assets/images/global-search/banner_block.png',
            title: quickStartLanguageMap[language].blockService,
            info: quickStartLanguageMap[language].blockServiceInfo,
            url: `interactive/iteach.html?language=${languageTemp}#${urlSuffix.blockService[type]}`,
            active: true,
        },
        {
            index: 1,
            type: 'quickstart',
            src: '/assets/images/global-search/banner_file_system.png',
            title: quickStartLanguageMap[language].fileService,
            info: quickStartLanguageMap[language].fileServiceInfo,
            url: `interactive/iteach.html?language=${languageTemp}#${urlSuffix.fileService[type]}`,
            active: false,
            hide: !SWITCH.SUPPORT_NAS,
        },
        {
            index: 2,
            type: 'quickstart',
            src: '/assets/images/global-search/banner_protection.png',
            title: quickStartLanguageMap[language].dataProtection,
            info: quickStartLanguageMap[language].dataProtectionInfo,
            url: `interactive/iteach.html?language=${languageTemp}#${urlSuffix.dataProtection[type]}`,
            active: false,
            hide: FeatureConfig.IsHideProtection || SWITCH.IS_HIDE_PROTECTION,
        },
        {
            index: 3,
            type: 'quickstart',
            src: '/assets/images/global-search/banner_management.png',
            title: quickStartLanguageMap[language].managemantAndMonitoring,
            info: quickStartLanguageMap[language].managemantAndMonitoringInfo,
            url: `interactive/iteach.html?language=${languageTemp}#${urlSuffix.managementAndMonitoring[type]}`,
            active: false,
        },
    ];
};
export const RECOMMENDED = {
    en: [
        {
            type: 'recommend',
            title: `${lun_en} Mapping YES`,
            advancedType: 'By Resource Status',
            license: [LicenseKey.SmartThin],
        },
        {
            type: 'recommend',
            title: 'Hosts HealthStatus = Normal',
            advancedType: 'By Resource Status',
            license: [LicenseKey.SmartThin],
        },
        {
            type: 'recommend',
            title: 'FileSystems HealthStatus != Faulty',
            advancedType: 'By Resource Status',
            isNASFeature: true,
        },
        {
            type: 'recommend',
            title: 'LogicalPorts ActivationStatus NO',
            advancedType: 'By Resource Status',
        },
        {
            type: 'recommend',
            title: 'vStores RunningStatus != Initializing',
            advancedType: 'By Resource Status',
            isNASFeature: true,
            license: [LicenseKey.SmartMultiTenant],
        },
        {
            type: 'recommend',
            title: `${lun_en} IOPS TOP15`,
            advancedType: 'By Performance or Capacity',
            // 微存储直通场景不支持性能监控
            isNotSupportFeatureInOceanMicro: SWITCH.IS_DIRECT_MODE,
            license: [LicenseKey.SmartThin],
        },
        {
            type: 'recommend',
            title: `${lun_en} Capacity > 100 `,
            subTitle: 'GB',
            advancedType: 'By Performance or Capacity',
            license: [LicenseKey.SmartThin],
        },
        {
            type: 'recommend',
            title: 'FileSystems OPS TOP15',
            advancedType: 'By Performance or Capacity',
            isNASFeature: true,
        },
        {
            type: 'recommend',
            title: 'Hosts Bandwidth BOTTOM15',
            // 微存储不支持性能监控
            isNotSupportFeatureInOceanMicro: FeatureConfig.IsHidePms,
            advancedType: 'By Performance or Capacity',
            license: [LicenseKey.SmartThin],
        },
        {
            type: 'recommend',
            title: 'LogicalPorts Latency TOP15',
            // 微存储不支持性能监控
            isNotSupportFeatureInOceanMicro: FeatureConfig.IsHidePms,
            advancedType: 'By Performance or Capacity',
        },
        {
            type: 'recommend',
            title: `LUNSnapshots Activated > ${moment().format('YYYY-MM')}-01`,
            advancedType: 'By Time',
            license: [LicenseKey.SmartThin, [LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP]],
        },
        {
            type: 'recommend',
            title: 'LUNSnapshots SourceLUN ',
            advancedType: 'By Associated Resource',
            license: [LicenseKey.SmartThin, [LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP]],
        },
        {
            type: 'recommend',
            title: 'LogicalPorts HomePort ',
            advancedType: 'By Associated Resource',
        },
    ],
    zh: [
        {
            type: 'recommend',
            title: `${lun_zh} 映射状态 是`,
            advancedType: '按资源状态',
            license: [LicenseKey.SmartThin],
        },
        {
            type: 'recommend',
            title: '主机 健康状态 = 正常',
            advancedType: '按资源状态',
            license: [LicenseKey.SmartThin],
        },
        {
            type: 'recommend',
            title: '文件系统 健康状态 != 故障',
            advancedType: '按资源状态',
            isNASFeature: true,
        },
        {
            type: 'recommend',
            title: '逻辑端口 激活状态 否',
            advancedType: '按资源状态',
        },
        {
            type: 'recommend',
            title: '多租户 运行状态 != 初始化中',
            advancedType: '按资源状态',
            isNASFeature: true,
            license: [LicenseKey.SmartMultiTenant],
        },
        {
            type: 'recommend',
            title: `${lun_zh} IOPS TOP15`,
            // 微存储直通场景不支持性能监控
            isNotSupportFeatureInOceanMicro: SWITCH.IS_DIRECT_MODE,
            advancedType: '按性能或容量',
            license: [LicenseKey.SmartThin],
        },
        {
            type: 'recommend',
            title: `${lun_zh} 容量 > 100 `,
            subTitle: 'GB',
            advancedType: '按性能或容量',
            license: [LicenseKey.SmartThin],
        },
        {
            type: 'recommend',
            title: '文件系统 OPS TOP15',
            advancedType: '按性能或容量',
            isNASFeature: true,
        },
        {
            type: 'recommend',
            title: '主机 带宽 BOTTOM15',
            // 微存储不支持性能监控
            isNotSupportFeatureInOceanMicro: FeatureConfig.IsHidePms,
            advancedType: '按性能或容量',
            license: [LicenseKey.SmartThin],
        },
        {
            type: 'recommend',
            title: '逻辑端口 时延 TOP15',
            // 微存储不支持性能监控
            isNotSupportFeatureInOceanMicro: FeatureConfig.IsHidePms,
            advancedType: '按性能或容量',
        },
        {
            type: 'recommend',
            title: `LUN快照 激活时间点 > ${moment().format('YYYY-MM')}-01`,
            advancedType: '按时间',
            license: [LicenseKey.SmartThin, [LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP]],
        },
        {
            type: 'recommend',
            title: 'LUN快照 源LUN ',
            advancedType: '按关联资源',
            license: [LicenseKey.SmartThin, [LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP]],
        },
        {
            type: 'recommend',
            title: '逻辑端口 主端口 ',
            advancedType: '按关联资源',
        },
    ],
};
export const DEFAULTRESOURCE = {
    en: [
        {
            title: lun_en,
            icon: '#infigo-lun',
            license: [LicenseKey.SmartThin],
        },
        {
            title: lun_group_en,
            icon: '#infigo-lunGroup',
            license: [LicenseKey.SmartThin],
        },
        {
            title: 'FileSystems',
            icon: '#infigo-fileSystem',
            isNASFeature: true,
        },
        {
            title: 'Hosts',
            icon: '#infigo-host',
            license: [LicenseKey.SmartThin],
        },
        {
            title: 'HostGroups',
            icon: '#infigo-hostGroup',
            license: [LicenseKey.SmartThin],
        },
        {
            title: 'vStores',
            icon: '#infigo-vStore',
            isNASFeature: true,
            license: [LicenseKey.SmartMultiTenant],
        },
        {
            title: 'LogicalPorts',
            icon: '#infigo-logicPort',
        },
        {
            title: 'PortGroups',
            icon: '#infigo-portGroup',
            license: [LicenseKey.SmartThin],
        },
        {
            title: 'LUNSnapshots',
            icon: '#infigo-snapShot',
            license: [LicenseKey.SmartThin, [LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP]],
        },
    ],
    zh: [
        {
            title: lun_zh,
            icon: '#infigo-lun',
            license: [LicenseKey.SmartThin],
        },
        {
            title: lun_group_zh,
            icon: '#infigo-lunGroup',
            license: [LicenseKey.SmartThin],
        },
        {
            title: '文件系统',
            icon: '#infigo-fileSystem',
            isNASFeature: true,
        },
        {
            title: '主机',
            icon: '#infigo-host',
            license: [LicenseKey.SmartThin],
        },
        {
            title: '主机组',
            icon: '#infigo-hostGroup',
            license: [LicenseKey.SmartThin],
        },
        {
            title: '多租户',
            icon: '#infigo-vStore',
            isNASFeature: true,
            license: [LicenseKey.SmartMultiTenant],
        },
        {
            title: '逻辑端口',
            icon: '#infigo-logicPort',
        },
        {
            title: '端口组',
            icon: '#infigo-portGroup',
            license: [LicenseKey.SmartThin],
        },
        {
            title: 'LUN快照',
            icon: '#infigo-snapShot',
            license: [LicenseKey.SmartThin, [LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP]],
        },
    ],
    ja: [
        {
            title: lun_ja,
            icon: '#infigo-lun',
            license: [LicenseKey.SmartThin],
        },
        {
            title: lun_group_ja,
            icon: '#infigo-lunGroup',
            license: [LicenseKey.SmartThin],
        },
        {
            title: 'ファイルシステム',
            icon: '#infigo-fileSystem',
            isNASFeature: true,
        },
        {
            title: 'ホスト',
            icon: '#infigo-host',
            license: [LicenseKey.SmartThin],
        },
        {
            title: 'HostGroups',
            icon: '#infigo-hostGroup',
            license: [LicenseKey.SmartThin],
        },
        {
            title: 'vStores',
            icon: '#infigo-vStore',
            isNASFeature: true,
            license: [LicenseKey.SmartMultiTenant],
        },
        {
            title: 'LogicalPorts',
            icon: '#infigo-logicPort',
        },
        {
            title: 'PortGroups',
            icon: '#infigo-portGroup',
            license: [LicenseKey.SmartThin],
        },
        {
            title: 'LUNスナップショット',
            icon: '#infigo-snapShot',
            license: [LicenseKey.SmartThin, [LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP]],
        },
    ],
};
export const DEFAULT_VALUE = '--';
export const DETAIL_LOADING_DELAY = 400;
export const IconType = {
    lun: '#infigo-lun',
    lunGroup: '#infigo-lunGroup',
    Namespace: '#infigo-lun',
    NamespaceGroup: '#infigo-lunGroup',
    lunSnapShot: '#infigo-snapShot',
    host: '#infigo-host',
    hostGroup: '#infigo-hostGroup',
    clone: '#infigo-lun',
    portGroup: '#infigo-portGroup',
    logicPort: '#infigo-logicPort',
    fileSystems: '#infigo-fileSystem',
    tenant: '#infigo-vStore',
    vvolLun: '#infigo-vvolLUN',
    peLun: '#infigo-vvolLUN',
    recycleBin: '#infigo-lun',
};
export const HASRSSOBJECT = [
    'SnapShot',
    'LunClone',
    'HyperCDP',
    'RemoteReplication',
    'HyperMetro',
    'drStar',
];
export const DEFAULT_STRING_VALUE = '--';

export interface SearchParams {
    searchKey?: string;
    name?: string;
    url?: string;
    type?: string;
    groupType?: GroupTypeE;
    isObject?: boolean;
    objectDes?: string;
    action?: string;
    actionParams?: string;
    tab?: string;
    show?: boolean;
}
